// Crab Report, two pages since Oct 26 (Jared: one page was too busy): 1) the next casting window at the crab spot,
// its conditions beside its Crab-o-meter and any beach hazard; 2) the high tides on the days ahead, each with its
// score, and the hand-kept season line. Conditions and timing, not regulations.
import tierOf, {
	SURF_ZONE, ended, dangers, alertWhen,
} from './advisories.mjs';
import { registerDisplay } from '../navigation.mjs';
import { escapeHtml } from '../utils/string.mjs';
import FeatureDisplay from './feature-display.mjs';
import { data, fetchedAt } from './snapshot.mjs';
import crabDays from './crab.mjs';
import {
	time, weekday, isToday, asOf, compass, feet, dayKey,
} from './format.mjs';

const { water, crabSeason } = window.RHL;
const DAY = 86400000;

const kv = (rows) => `<div class="kv kv3">${rows.filter(Boolean).map(([k, v, verdict]) => `<div>${k}</div><div class="v">${v}</div><div class="w">${verdict ?? ''}</div>`).join('')}</div>`;
// the score's colour says good or bad, not its size (Oct 27, Jared): 7+ green, 4-6 yellow, 3 or less red
const scoreLevel = (score) => {
	if (score >= 7) return 'lv-good';
	return score >= 4 ? 'lv-fair' : 'lv-poor';
};
const meter = (score) => `<div class="meter">${Array.from({ length: 10 }, (_, i) => `<i class="${i < score ? 'on' : ''}"></i>`).join('')}</div>`;
const dayLabel = (t, now) => (isToday(t, now) ? 'TODAY' : weekday(t));

const seasonLine = (now) => {
	if (!crabSeason) return '';
	const days = Math.round((new Date(`${crabSeason.opener}T12:00:00`) - new Date(`${dayKey(now)}T12:00:00`)) / DAY);
	const countdown = days > 0 ? `${crabSeason.openerLabel} (${days} day${days === 1 ? '' : 's'})` : null;
	return [countdown, ...crabSeason.notes].filter(Boolean).map(escapeHtml).join(' &middot; ');
};

class CrabReport extends FeatureDisplay {
	constructor(navId, elemId) {
		super(navId, elemId, 'Crab Report');
	}

	// eslint-disable-next-line class-methods-use-this
	hasData() {
		return crabDays({ tides: data('tides') }).length > 0;
	}

	// eslint-disable-next-line class-methods-use-this
	buildPages(now) {
		const swell = data('swell');
		const wind = data('wind');
		const days = crabDays({ tides: data('tides'), swell, wind }, now);
		if (!days.length) return [];
		const [next, ...ahead] = days;
		const { high } = next;

		const light = (() => {
			if (next.end <= next.sun.rise || next.start >= next.sun.set) return 'after dark';
			if (next.end > next.sun.set) return `sunset ${time(next.sun.set)}`;
			if (next.start < next.sun.rise) return `sunrise ${time(next.sun.rise)}`;
			return 'daylight';
		})();
		// label, value, verdict
		const rows = [
			['High', `${time(high.t)} ${feet(high.ft)}`],
			['Window', `${time(next.start)} to ${time(next.end)}`],
			next.swell && ['Surf', `${Math.round(next.swell.ft)} ft ${Math.round(next.swell.periodS)} s${next.powerKj !== null ? ` ${next.powerKj} kJ` : ''}`, next.swell.word],
			next.currentKt != null && ['Current', `${next.currentKt.toFixed(1)} kt`],
			next.wind && ['Wind', `${compass(next.wind.dirDeg)} ${Math.round(next.wind.kt)} kt${next.wind.onshore ? ' onshore' : ''}`, next.wind.word],
			['Light', light],
		];

		const hazard = (data('alerts') ?? []).find((a) => !ended(a, now) && (tierOf(a.event) === 'crab' || (a.zones.includes(SURF_ZONE) && tierOf(a.event) === 'crawl')));
		const hazardLine = hazard ? `<div class="alert-line gap">${escapeHtml(dangers(hazard))}</div><div class="small">${escapeHtml(alertWhen(hazard, now))}</div>` : '';
		const forecastAt = swell ? `Forecast ${asOf(fetchedAt('swell'), now)}` : 'No swell forecast';

		const pages = [{
			seconds: 12,
			title: ['Crab', 'Report'],
			html: `<div class="cols crab">
				<div>
					<div class="hdr">${escapeHtml(water.crabSpot.name)} &middot; ${dayLabel(high.t, now)}</div>
					${kv(rows)}
				</div>
				<div class="crab-score">
					<div class="hdr">Crab-o-meter</div>
					<div class="crab-big"><b class="${scoreLevel(next.score)}">${next.score}</b>/10</div>
					${meter(next.score)}
					${hazardLine}
				</div>
			</div>
			<div class="asof">${forecastAt}${next.complete ? '' : ' &middot; partial score'}</div>`,
		}];

		const season = seasonLine(now);
		if (ahead.length || season) {
			const aheadRows = ahead.slice(0, 4).map((d) => `<div class="row"><div class="t">${weekday(d.high.t)}</div><div>${time(d.high.t)}</div><div>${Math.round(d.high.ft)} ft</div><div>${meter(d.score)}</div><div class="sc">${d.score}/10</div></div>`);
			pages.push({
				seconds: 12,
				title: ['Crab', 'Report'],
				html: `<div class="hdr">High tides ahead &middot; ${escapeHtml(water.crabSpot.name)}</div>
					<div class="list ahead wide-ahead"><div class="row th"><div>Day</div><div>High tide</div><div>Height</div><div>Crab-o-meter</div><div></div></div>${aheadRows.join('')}</div>
					<div class="small dim season">${season}</div>
					<div class="asof">${forecastAt}</div>`,
			});
		}
		return pages;
	}
}

const display = new CrabReport(15, 'crab-report');
display.makeLoopScreen();
registerDisplay(display);
